import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, ProtectedRoute, RoleGate } from "@/lib/auth";
import { ThemeProvider, useTheme } from "@/lib/theme";
import Layout from "@/components/Layout";
import FloatingChat from "@/components/FloatingChat";

// PR-Cg-Polish-2 Item 6 — code-split every route component so the
// initial JS bundle ships only the chrome (Layout, AuthProvider,
// ThemeProvider, Toaster, FloatingChat) + the immediately-visible
// route (Login on a cold load, Dashboard once authenticated).
// Subsequent routes pull their chunks on first navigation. Login
// stays eager so the cold-start auth flow doesn't show a flash of
// Suspense fallback before the form paints.
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Settings = lazy(() => import("@/pages/Settings"));
const SettingsTeam = lazy(() => import("@/pages/SettingsTeam"));
const SettingsNotifications = lazy(() => import("@/pages/SettingsNotifications"));
const Leads = lazy(() => import("@/pages/Leads"));
const LeadDetail = lazy(() => import("@/pages/LeadDetail"));
const FollowUps = lazy(() => import("@/pages/FollowUps"));
const Quotes = lazy(() => import("@/pages/Quotes"));
const QuoteEditor = lazy(() => import("@/pages/QuoteEditor"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const InvoiceEditor = lazy(() => import("@/pages/InvoiceEditor"));
const Projects = lazy(() => import("@/pages/Projects"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const ProjectGroupDetail = lazy(() => import("@/pages/ProjectGroupDetail"));
const Today = lazy(() => import("@/pages/Today"));
const Expenses = lazy(() => import("@/pages/Expenses"));
// PR-Cg-Round6 — OWNER-only Overhead / Company Expenses surface.
const Overhead = lazy(() => import("@/pages/Overhead"));
const ReferenceLibrary = lazy(() => import("@/pages/ReferenceLibrary"));
// PR-Cg-WarrantyTemplatesSettingsUI — OWNER-only CRUD for the 31
// preset warranty templates + custom operator-created ones.
const SettingsWarrantyTemplates = lazy(() => import("@/pages/SettingsWarrantyTemplates"));
const Insights = lazy(() => import("@/pages/Insights"));
const Meetings = lazy(() => import("@/pages/Meetings"));
const MeetingsCalendar = lazy(() => import("@/pages/MeetingsCalendar"));
// PR-Cg-Phase4 — standalone Project Schedule Calendar (additive to
// GCal sync). Read-only rollup of ``project_schedules`` for a
// month / week view of every scheduled project across the shop.
const ScheduleCalendar = lazy(() => import("@/pages/ScheduleCalendar"));
const MeetingDetail = lazy(() => import("@/pages/MeetingDetail"));
const AiChat = lazy(() => import("@/pages/AiChat"));
const EmailLogs = lazy(() => import("@/pages/EmailLogs"));
const IntakeLogs = lazy(() => import("@/pages/IntakeLogs"));
const Payroll = lazy(() => import("@/pages/Payroll"));
const PayrollCommissions = lazy(() => import("@/pages/PayrollCommissions"));
const RepProfile = lazy(() => import("@/pages/RepProfile"));
const PayeeProfile = lazy(() => import("@/pages/PayeeProfile"));
const AdminRepAttributionAudit = lazy(() => import("@/pages/AdminRepAttributionAudit"));
const AdminLeadAttributionAudit = lazy(() => import("@/pages/AdminLeadAttributionAudit"));
const AdminQuoteRepBackfill = lazy(() => import("@/pages/AdminQuoteRepBackfill"));
const AdminOrphanLeads = lazy(() => import("@/pages/AdminOrphanLeads"));
const AdminQuoteInvoiceBackfill = lazy(() => import("@/pages/AdminQuoteInvoiceBackfill"));
const AdminLegacyNotesMigration = lazy(() => import("@/pages/AdminLegacyNotesMigration"));
// PR-Cg-EmailRoutingAudit — OWNER-only visibility over outbound
// email routing (which template rendered, which sender identity,
// what SendGrid did with it).
const AdminEmailRoutingAudit = lazy(() => import("@/pages/AdminEmailRoutingAudit"));
// PR-Cg-MaintenanceConsole — OWNER-only unified admin surface.
// Registry-driven console that surfaces every one-shot admin
// endpoint with a preview/apply flow + shared runs history.
const MaintenanceConsole = lazy(() => import("@/pages/MaintenanceConsole"));
const BackfillWorkTypes = lazy(() => import("@/pages/BackfillWorkTypes"));
const MediaLibrary = lazy(() => import("@/pages/MediaLibrary"));
const CostAnalysis = lazy(() => import("@/pages/CostAnalysis"));
const PublicQuoteView = lazy(() => import("@/pages/PublicQuoteView"));
const PublicInvoiceView = lazy(() => import("@/pages/PublicInvoiceView"));
const PortalHome = lazy(() => import("@/pages/portal/PortalHome"));
const PortalProject = lazy(() => import("@/pages/portal/PortalProject"));
const PortalQuote = lazy(() => import("@/pages/portal/PortalQuote"));
const PortalInvoice = lazy(() => import("@/pages/portal/PortalInvoice"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));

// Sonner needs a concrete theme value (light|dark) — pull from context.
function ThemedToaster() {
  const { resolvedTheme } = useTheme();
  return (
    <Toaster
      theme={resolvedTheme}
      position="top-right"
      toastOptions={{
        classNames: {
          toast:
            "!bg-card !border !border-border !text-foreground !rounded-sm !font-medium",
          description: "!text-muted-foreground",
          success: "!border-[rgba(245,130,22,0.4)]",
          error: "!border-[rgba(239,68,68,0.5)]",
          actionButton: "!bg-[#F58216] !text-black",
        },
      }}
    />
  );
}

// PR-Cg-Polish-2 Item 6 — minimal Suspense fallback. The Layout
// already paints a full chrome (sidebar + header) so this only
// covers the route slot. Stays muted so a fast network barely
// notices the flash; on slow networks the operator sees a calm
// pulse instead of a blank rectangle.
function RouteFallback() {
  return (
    <div
      className="p-6 space-y-3"
      role="status"
      aria-label="Loading page…"
      data-testid="route-suspense-fallback"
    >
      <div className="h-8 w-1/3 rounded-sm bg-secondary/60 animate-pulse" />
      <div className="h-4 w-1/2 rounded-sm bg-secondary/40 animate-pulse" />
      <div className="h-64 rounded-sm bg-secondary/30 animate-pulse" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            {/* Single outer Suspense boundary handles every lazy
                route. Nested boundaries would let the chrome paint
                while the route loads, but we already render the
                chrome via <Layout> — so a single outer fallback
                is the smallest cut that still hits the bundle
                size goal. */}
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                {/* Phase 7A — Public unauthenticated views (no chrome). */}
                <Route path="/public/quotes/:token" element={<PublicQuoteView />} />
                <Route path="/public/invoices/:token" element={<PublicInvoiceView />} />
                {/* Phase G1 — Customer portal (token-gated, no chrome). */}
                <Route path="/portal/:token" element={<PortalHome />} />
                <Route path="/portal/:token/projects/:id" element={<PortalProject />} />
                <Route path="/portal/:token/quotes/:id" element={<PortalQuote />} />
                <Route path="/portal/:token/invoices/:id" element={<PortalInvoice />} />
                {/* Phase 9 — Public invite-acceptance / password-reset pages. */}
                <Route path="/accept-invite" element={<AcceptInvite mode="invite" />} />
                <Route path="/reset-password" element={<AcceptInvite mode="reset" />} />
                <Route element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route path="/dashboard" element={<Dashboard />} />
                  {/* PR-Cf — Leads / Quotes / Invoices / Insights are
                      OWNER+REP only. CREW / CREW_LEAD redirect to
                      /dashboard (the backend would 403 the calls
                      anyway; this avoids the empty-state flash). */}
                  <Route path="/leads" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <Leads />
                    </RoleGate>
                  } />
                  <Route path="/leads/new" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <Leads />
                    </RoleGate>
                  } />
                  <Route path="/leads/:id" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <LeadDetail />
                    </RoleGate>
                  } />
                  <Route path="/quotes" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <Quotes />
                    </RoleGate>
                  } />
                  <Route path="/quotes/new" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <QuoteEditor />
                    </RoleGate>
                  } />
                  <Route path="/quotes/:id" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <QuoteEditor />
                    </RoleGate>
                  } />
                  <Route path="/invoices" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <Invoices />
                    </RoleGate>
                  } />
                  <Route path="/invoices/new" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <InvoiceEditor />
                    </RoleGate>
                  } />
                  {/* PR-Cf.4 — invoice DETAIL is open to CREW + CREW_LEAD
                      so they can read the customer-facing invoice tied
                      to a project they're assigned to. The list stays
                      OWNER+REP (CREW shouldn't browse the full ledger). */}
                  <Route path="/invoices/:id" element={
                    <RoleGate roles={["OWNER", "REP", "CREW", "CREW_LEAD"]} fallback={<Navigate to="/dashboard" replace />}>
                      <InvoiceEditor />
                    </RoleGate>
                  } />
                  <Route path="/projects" element={<Projects />} />
                  <Route path="/projects/new" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <ProjectDetail />
                    </RoleGate>
                  } />
                  <Route path="/projects/:id" element={<ProjectDetail />} />
                  {/* PR-Ca.1 — Project Groups detail page (Phase 1).
                      PR-Cf.4 — opened to CREW so they can see group context
                      for any project they're assigned to. The backend
                      already 403s on groups whose members the caller
                      can't see. */}
                  <Route path="/project-groups/:id" element={
                    <RoleGate roles={["OWNER", "REP", "CREW", "CREW_LEAD"]} fallback={<Navigate to="/projects" replace />}>
                      <ProjectGroupDetail />
                    </RoleGate>
                  } />
                  <Route path="/today" element={<Today />} />
                  <Route path="/expenses" element={
                    <RoleGate roles={["OWNER", "REP", "CREW", "CREW_LEAD"]} fallback={<Navigate to="/dashboard" replace />}>
                      <Expenses />
                    </RoleGate>
                  } />
                  {/* PR-Cg-Round6 — Overhead / Company Expenses.
                      OWNER-only. Non-owner → /dashboard (also the same
                      pattern the backend enforces via 403 on every
                      /api/overheads/* endpoint). */}
                  <Route path="/overhead" element={
                    <RoleGate roles={["OWNER"]} fallback={<Navigate to="/dashboard" replace />}>
                      <Overhead />
                    </RoleGate>
                  } />
                  <Route path="/meetings" element={<Meetings />} />
                  {/* E5 — full-page calendar of meeting_bookings.
                      Distinct from `/meetings` (audio recordings list)
                      which stays accessible from its own nav link. */}
                  <Route path="/meetings/calendar" element={<MeetingsCalendar />} />
                  {/* PR-Cg-Phase4 — Standalone project schedule calendar. */}
                  <Route path="/schedule/calendar" element={<ScheduleCalendar />} />
                  <Route path="/meetings/new" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <MeetingDetail />
                    </RoleGate>
                  } />
                  <Route path="/meetings/:id" element={<MeetingDetail />} />
                  <Route path="/insights" element={
                    <RoleGate roles={["OWNER", "REP", "CREW", "CREW_LEAD"]} fallback={<Navigate to="/dashboard" replace />}>
                      <Insights />
                    </RoleGate>
                  } />
                  <Route
                    path="/payroll"
                    element={
                      <RoleGate
                        roles={["OWNER", "REP", "CREW", "CREW_LEAD"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <Payroll />
                      </RoleGate>
                    }
                  />
                  {/* PR-Payroll — Rep commissions payroll surface. */}
                  <Route
                    path="/payroll/commissions"
                    element={
                      <RoleGate
                        roles={["OWNER", "REP"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <PayrollCommissions />
                      </RoleGate>
                    }
                  />
                  {/* PR-Payroll — Owner-only per-rep profile + scoped
                      commission table. */}
                  <Route
                    path="/team/reps/:rep_id"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <RepProfile />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="/email-logs"
                    element={
                      <RoleGate
                        roles={["OWNER", "REP"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <EmailLogs />
                      </RoleGate>
                    }
                  />
                  {/* PR-Cf — AI chat is OWNER-only this iteration.
                      REP/CREW_LEAD/CREW redirect to /dashboard since
                      the backend gate would return 403 on every call. */}
                  <Route path="/ai" element={
                    <RoleGate roles={["OWNER"]} fallback={<Navigate to="/dashboard" replace />}>
                      <AiChat />
                    </RoleGate>
                  } />
                  <Route path="/follow-ups" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <FollowUps />
                    </RoleGate>
                  } />
                  {/* PR-Ck — Media Library. OWNER + REP only. CREW +
                      CREW_LEAD redirect to /dashboard (the backend
                      would 403 the calls anyway; this avoids the
                      empty-state flash and keeps the route blind to
                      deep-link probing). */}
                  <Route path="/library" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <MediaLibrary />
                    </RoleGate>
                  } />
                  {/* PR-Cl Phase 2.5 — Materials renamed +
                      restructured into a Cost & Profit Analysis
                      page with Materials + Labor tabs. The old
                      /materials URL redirects to /cost-analysis
                      for backwards-compat. */}
                  <Route path="/cost-analysis" element={
                    <RoleGate roles={["OWNER", "REP"]} fallback={<Navigate to="/dashboard" replace />}>
                      <CostAnalysis />
                    </RoleGate>
                  } />
                  <Route path="/materials" element={
                    <Navigate to="/cost-analysis" replace />
                  } />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/team" element={<SettingsTeam />} />
                  <Route path="/settings/notifications" element={<SettingsNotifications />} />
                  <Route
                    path="/settings/team/crew/:id"
                    element={<PayeeProfile variant="crew" />}
                  />
                  <Route
                    path="/settings/team/sub/:id"
                    element={
                      <RoleGate
                        roles={["OWNER", "REP"]}
                        fallback={<Navigate to="/settings/team" replace />}
                      >
                        <PayeeProfile variant="sub" />
                      </RoleGate>
                    }
                  />
                  <Route path="/settings/reference-library" element={<ReferenceLibrary />} />
                  {/* PR-Cg-WarrantyTemplatesSettingsUI — OWNER-only. */}
                  <Route
                    path="/settings/warranty-templates"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/settings" replace />}
                      >
                        <SettingsWarrantyTemplates />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="/admin/rep-attribution"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <AdminRepAttributionAudit />
                      </RoleGate>
                    }
                  />
                  {/* PR-Bugfix-2 Bug 2 — OWNER-only Lead Attribution Audit. */}
                  <Route
                    path="/admin/lead-attribution"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <AdminLeadAttributionAudit />
                      </RoleGate>
                    }
                  />
                  {/* PR-Email-Identity Part C — OWNER-only quote rep
                      inheritance backfill. */}
                  <Route
                    path="/admin/quote-rep-backfill"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <AdminQuoteRepBackfill />
                      </RoleGate>
                    }
                  />
                  {/* PR-E — Owner-only orphan lead restoration UI. */}
                  <Route
                    path="/admin/orphan-leads"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <AdminOrphanLeads />
                      </RoleGate>
                    }
                  />
                  {/* PR-Cg-BackfillAdminUI — Owner-only quote/invoice
                      backfill UI wrapping the
                      scripts/pr_cg_quote_invoice_backfill.py flow. */}
                  <Route
                    path="/admin/quote-invoice-backfill"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <AdminQuoteInvoiceBackfill />
                      </RoleGate>
                    }
                  />
                  {/* PR-Cg-LegacyNotesAdminUI — Owner-only legacy
                      notes migration UI. P0 ship for the prod
                      redeploy that left existing leads/quotes/
                      projects with empty timelines until their
                      legacy notes are migrated into the new
                      collection. */}
                  <Route
                    path="/admin/legacy-notes-migration"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <AdminLegacyNotesMigration />
                      </RoleGate>
                    }
                  />
                  {/* PR-Cg-EmailRoutingAudit — Owner-only visibility
                      over outbound email routing. Route lands the
                      operator on a filterable table with export-to-CSV. */}
                  <Route
                    path="/admin/email-routing-audit"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <AdminEmailRoutingAudit />
                      </RoleGate>
                    }
                  />
                  {/* PR-Cg-MaintenanceConsole — Owner-only unified admin
                      surface. Registry-driven; ships preview/apply for
                      every one-shot admin endpoint + shared audit trail. */}
                  <Route
                    path="/admin/maintenance-console"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/settings" replace />}
                      >
                        <MaintenanceConsole />
                      </RoleGate>
                    }
                  />
                  {/* Phase ANL-1 — Owner-only work types backfill queue. */}
                  <Route
                    path="/backfill/work-types"
                    element={
                      <RoleGate
                        roles={["OWNER"]}
                        fallback={<Navigate to="/dashboard" replace />}
                      >
                        <BackfillWorkTypes />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="/settings/integrations/intake-logs"
                    element={
                      <RoleGate
                        roles={["OWNER", "REP"]}
                        fallback={<Navigate to="/settings" replace />}
                      >
                        <IntakeLogs />
                      </RoleGate>
                    }
                  />
                </Route>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
            <FloatingChat />
            <ThemedToaster />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
